import type { ApiError } from './client';

export const emailPattern = {
  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
  message: 'Invalid email address',
};

export const namePattern = {
  value: /^[A-Za-z\s\u00C0-\u017F]{1,30}$/,
  message: 'Invalid name',
};

export function stringToColor(str: string): string {
  if (!str) return 'gray';
  const colors = ['gray', 'red', 'green', 'blue', 'teal', 'pink', 'purple', 'cyan', 'orange', 'yellow'];

  // Simple hash function to convert string to number
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert hash to positive number and map to colors array index
  const index = Math.abs(hash) % colors.length;
  return `${colors[index]}`;
}

export const passwordRules = (isRequired = true) => {
  const rules: any = {
    minLength: {
      value: 8,
      message: 'Password must be at least 8 characters',
    },
  };

  if (isRequired) {
    rules.required = 'Password is required';
  }

  return rules;
};

export const confirmPasswordRules = (getValues: () => any, isRequired = true) => {
  const rules: any = {
    validate: (value: string) => {
      const password = getValues().password || getValues().new_password;
      return value === password ? true : 'The passwords do not match';
    },
  };

  if (isRequired) {
    rules.required = 'Password confirmation is required';
  }

  return rules;
};

export const handleError = (err: ApiError, showToast: any) => {
  const errDetail = (err.body as any)?.detail;
  let errorMessage = errDetail || 'Something went wrong.';
  if (Array.isArray(errDetail) && errDetail.length > 0) {
    errorMessage = errDetail[0].msg;
  }
  showToast('Error', errorMessage, 'error');
};

/**
 * Generate a string representing the name of a collecte period.
 * @param year The year of the collecte period
 * @param period_number The number of the period (1, 2, 3, 4)
 * @param period The type of period ('year', 'semester', 'trimester')
 * @returns A string representing the name of the collecte period, e.g. 'Année 2022', 'Janvier à Février 2022', 'Mars à Avril 2022'
 */
export const generateCollecteName = (year: number, period_number: number, period: string) => {
  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  if (period === 'year') {
    return `Année ${year}`;
  }

  if (period === 'semester') {
    const semesters: Record<number, [number, number]> = {
      1: [0, 5],
      2: [6, 11],
    };
    const [start, end] = semesters[period_number];
    return `${monthNames[start]} à ${monthNames[end]} ${year}`;
  }

  if (period === 'trimester') {
    const quarters: Record<number, [number, number]> = {
      1: [0, 2],
      2: [3, 5],
      3: [6, 8],
      4: [9, 11],
    };
    const [start, end] = quarters[period_number];
    return `${monthNames[start]} à ${monthNames[end]} ${year}`;
  }

  return `Période ${period} ${year}`;
};

/**
 * Generate a string representing the months of a period.
 * @param period_number The number of the period (1, 2, 3, 4)
 * @param period The type of period ('year', 'semester', 'trimester')
 * @returns A string representing the months of the period, e.g. 'Janvier-Février'
 */
export const generatePeriodMonths = (period_number: number, period: 'year' | 'semester' | 'trimester'): string => {
  const monthNames = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
  ];

  let start = 0;
  let end = 11;

  if (period === 'semester') {
    const semesters: Record<number, [number, number]> = {
      1: [0, 5],
      2: [6, 11],
    };
    [start, end] = semesters[period_number];
  } else if (period === 'trimester') {
    const trimesters: Record<number, [number, number]> = {
      1: [0, 2],
      2: [3, 5],
      3: [6, 8],
      4: [9, 11],
    };
    [start, end] = trimesters[period_number];
  }

  return `${monthNames[start]}-${monthNames[end]}`;
};

/**
 * Convert a string to snake case.
 *
 * Example: "HelloWorld" -> "hello_world"
 *
 * @param {string} str - The string to convert.
 * @returns {string} The converted string in snake case.
 */
export const snakeCaseString = (str: string): string => {
  if (!str) return '';
  const matches = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g) || [];
  return matches.map((s) => s.toLowerCase()).join('_');
};
